import {useMemo} from 'react';
import {useGetProductGroupById} from '@tinct/core-api';
import {PriceInterval, ReadPrice, ReadProduct} from '@tinct/core-api-models';
import {type PlanFeature} from '@tinct/web-components';
import {formatPrice, PRODUCT_GROUP_FIELDS} from '../../../hooks/use-plan-options';

const STARTER_FEATURES: PlanFeature[] = [
  {name: '25 landing page variants'},
  {name: '50 AI personalization credits'},
  {name: 'Leads enrichment'},
  {name: 'Unlimited visits'},
  {name: 'Unlimited users'},
  {name: 'CRM Integration', included: false},
  {name: 'AI block editing', included: false},
];

const PRO_FEATURES: PlanFeature[] = [
  {name: '25 landing page variants'},
  {name: '75 AI personalization credits'},
  {name: 'Leads enrichment'},
  {name: 'Unlimited visits'},
  {name: 'Unlimited users'},
  {name: 'CRM Integration'},
  {name: 'AI block editing'},
  {name: 'User role management'},
  {name: 'Premium support'},
];

export function usePaywallProducts(
  selectedProductId: string | null,
  isAnnual: boolean,
) {
  const { data: productGroup } = useGetProductGroupById({
    id: 'pg_pro',
    fields: PRODUCT_GROUP_FIELDS,
  });

  const products = useMemo(
    () => ((productGroup?.products ?? []) as ReadProduct[]).filter(
      (p) => p.name?.toLowerCase().includes('starter') || p.name?.toLowerCase().includes('pro'),
    ),
    [productGroup],
  );

  const currentInterval: PriceInterval = isAnnual ? 'year' : 'month';

  const planOptions = useMemo(() => {
    return products
      .filter((p) => p.id != null)
      .map((product) => {
        const name = product.name?.toLowerCase().includes('pro') ? 'Pro' : 'Starter';
        const label = product.quotas?.lpPublication
          ? `${name}: ${product.quotas.lpPublication} landing page variants`
          : product.name ?? product.id ?? '';
        return { label, value: product.id as string };
      });
  }, [products]);

  const selectedProduct = useMemo(() => {
    if (selectedProductId) {
      const found = products.find((p) => p.id === selectedProductId);
      if (found) return found;
    }
    return products[0] ?? null;
  }, [products, selectedProductId]);

  const prices = useMemo((): ReadPrice[] => {
    return ((selectedProduct?.prices ?? []) as ReadPrice[]).filter(
      (p) => p.active,
    );
  }, [selectedProduct]);

  const selectedPrice = useMemo((): ReadPrice | null => {
    return prices.find((p) => p.recurringInterval === currentInterval) ?? prices[0] ?? null;
  }, [prices, currentInterval]);

  const monthlyAmountInCents = selectedPrice?.unitAmountInCents != null && selectedPrice.recurringInterval === 'year'
    ? Math.round(selectedPrice.unitAmountInCents / 12)
    : selectedPrice?.unitAmountInCents;

  const displayPrice = selectedPrice
    ? formatPrice(monthlyAmountInCents, selectedPrice.currency)
    : undefined;

  const annualSavings = useMemo((): string | undefined => {
    const monthlyPrice = prices.find((p) => p.recurringInterval === 'month');
    const yearlyPrice = prices.find((p) => p.recurringInterval === 'year');
    if (monthlyPrice?.unitAmountInCents == null || yearlyPrice?.unitAmountInCents == null) return undefined;
    const savingInCents = (monthlyPrice.unitAmountInCents * 12) - yearlyPrice.unitAmountInCents;
    if (savingInCents <= 0) return undefined;
    return `Save ${formatPrice(savingInCents, yearlyPrice.currency)}`;
  }, [prices]);

  const isSelectedPro = selectedProduct?.name?.toLowerCase().includes('pro') ?? false;

  const features = useMemo((): PlanFeature[] => {
    const staticFeatures = isSelectedPro ? PRO_FEATURES : STARTER_FEATURES;
    const quotas = selectedProduct?.quotas;
    if (!quotas) return staticFeatures;
    return staticFeatures.map((f) => {
      if (f.name.includes('landing page variants') && quotas.lpPublication != null) {
        return {...f, name: `${quotas.lpPublication} landing page variants`};
      }
      if (f.name.includes('AI personalization credits') && quotas.lpGenerationCredit != null) {
        return {...f, name: `${quotas.lpGenerationCredit} AI personalization credits`};
      }
      return f;
    });
  }, [selectedProduct, isSelectedPro]);

  return {
    planOptions,
    selectedProduct,
    selectedPrice,
    displayPrice,
    annualSavings,
    features,
  };
}
